import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  type RenderableText,
} from 'recharts';
import Typhography from '../../../shared/Typhography/Typhography';
import styles from './MethodDistributionChart.module.scss';
import type { IMethodAggregate } from '../../../models/trainingStatistics';

/**
 * Sezione 3 — Distribuzione per Metodo Didattico (barre orizzontali).
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md righe 2827-2839.
 */
interface MethodDistributionChartProps {
  data: IMethodAggregate[];
  methodLabels?: Record<string, string>;
}

const METHOD_COLORS: Record<string, string> = {
  analytical: '#063444',
  global: '#0f5c7a',
  situational: '#1a8fb0',
  themed_game: '#ffd15a',
  free_game: '#f59e0b',
};

const DEFAULT_METHOD_LABELS: Record<string, string> = {
  analytical: 'Analitico',
  global: 'Globale',
  situational: 'Situazionale',
  themed_game: 'Partita a tema',
  free_game: 'Partita libera',
};

const fmtMinutes = (m: number) => {
  const h = Math.floor(m / 60);
  const min = m % 60;
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
};

const MethodDistributionChart: React.FC<MethodDistributionChartProps> = ({
  data,
  methodLabels = {},
}) => {
  if (!data || data.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Typhography size="regular-14" className={styles.emptyText}>
          Nessun dato disponibile per i metodi didattici.
        </Typhography>
      </div>
    );
  }

  const labels = { ...DEFAULT_METHOD_LABELS, ...methodLabels };

  const chartData = data
    .sort((a, b) => b.minutes - a.minutes)
    .map((d) => ({
      name: labels[d.methodId] ?? d.methodId,
      id: d.methodId,
      minutes: d.minutes,
      percentage: d.percentage,
    }));

  return (
    <div className={styles.container}>
      <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 52)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 4, right: 60, bottom: 4, left: 120 }}
        >
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 13, fill: '#00212c', fontFamily: 'Satoshi, sans-serif' }}
            width={110}
          />
          <Tooltip
            formatter={(value) => [`${fmtMinutes(Number(value))}`, 'Tempo']}
          />
          <Bar dataKey="minutes" radius={[0, 6, 6, 0]} label={{ position: 'right', formatter: (v: RenderableText) => `${Math.round((Number(v) / (data.reduce((s, d) => s + d.minutes, 0) || 1)) * 100)}%`, fontSize: 12, fill: '#5a5a5a' }}>
            {chartData.map((entry) => (
              <Cell
                key={entry.id}
                fill={METHOD_COLORS[entry.id] ?? '#063444'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MethodDistributionChart;
